import { useEffect, useRef, useState } from "react";
import { FloatButton, Input } from "antd";
import { MessageOutlined, CloseOutlined, SendOutlined } from "@ant-design/icons";
import { useIntl } from "react-intl";
import { v4 as uuidv4 } from "uuid";
import { DateTime } from "luxon";
import Supabase from "src/utility/supabase";
import { useSelector } from "src/redux/store";
import {
    ContactWindow, ContactHeader, ContactContent,
    MessageWindow, MessageHeader, MessageContent, MessageFooter
} from "./components";

export default () => {
    const [supabase, setSupabase] = useState();
    const [open, setOpen] = useState();
    const [channel, setChannel] = useState();
    const [message, setMessage] = useState();
    const [userId, setUserId] = useState();
    const [users, setUsers] = useState([]);
    const { formatMessage } = useIntl();
    const { keycloak } = useSelector((state) => state.keycloakReducer);
    const msgRef = useRef();
    const datetimeRef = useRef({ date: undefined, time: undefined });

    const initChatPanel = async () => {
        if (keycloak.authenticated) {
            // Init contact list
            getContact();

            // Init channels
            subscribeChannel();
        } else {
            let channelId = localStorage.getItem("channel");
            if (channelId) {
                // Init user
                await getUserByChannel(channelId);
            } else {
                channelId = uuidv4();
                localStorage.setItem("channel", channelId);

                // Init user
                await createUser({ channel: channelId });

                // Create welcome message
                await sendMessage({
                    channel: channelId,
                    message: "Welcome to my personal website! Please leave message and I'll reply you when I see it :)"
                });
            }

            // Init channel
            setChannel(channelId);
        }
    };

    const sendMessage = async (data) => {
        if (data || message) {
            const res = await supabase.getClient()
                .from("messages")
                .insert(data || { channel, message, user: userId });
            if (res.status == 201) setMessage();
            else console.log("Send message failed:", res);
        }
    };

    const readMessages = async () => {
        const statusIcon = document.querySelector(`div[channel="${channel}"] .status-icon`);
        if (statusIcon) {
            const res = await supabase.getClient()
                .from("messages")
                .update({ is_read: 1 })
                .eq("channel", channel)
                .eq("is_read", 0);
            if (res.status == 204) statusIcon.parentNode.innerHTML = "";
            else console.log("Updat message failed:", res);
        }
    };

    const getMessages = async () => {
        while (msgRef.current.hasChildNodes()) msgRef.current.removeChild(msgRef.current.firstChild);
        const res = await supabase.getClient()
            .from("messages")
            .select("user,message,created_at")
            .eq("channel", channel)
            .order("id");
        if (res.status == 200) renderMessages(res.data);
        else console.log("Get messages failed:", res);
    };

    const renderMessages = (data) => {
        let prevDateDiv = datetimeRef.current.date;
        let prevTimeDiv = datetimeRef.current.time;
        data.forEach((d) => {
            // Init data
            const datetime = DateTime.fromISO(d.created_at);
            const currentDate = datetime.toFormat("yyyy/M/d (EEE)", { locale: localStorage.getItem("locale") });
            const currentTime = datetime.toFormat("H:m");

            // Date
            if (prevDateDiv?.getAttribute("date") != currentDate) {
                const dateDiv = document.createElement("div");
                dateDiv.className = "date";
                dateDiv.setAttribute("date", currentDate);
                if (datetime.hasSame(DateTime.now(), "day")) {
                    dateDiv.innerHTML = formatMessage({ id: "today" });
                    if (prevDateDiv) prevDateDiv.innerHTML = prevDateDiv.getAttribute("date");
                } else {
                    dateDiv.innerHTML = currentDate;
                }
                msgRef.current.append(dateDiv);
                prevDateDiv = dateDiv;
            }

            // Row
            const rowDiv = document.createElement("div");
            rowDiv.className = d.user == userId ? "row right" : "row left";

            // Dialog
            const dialogDiv = document.createElement("div");
            dialogDiv.className = d.user == userId ? "dialog right" : "dialog left";
            dialogDiv.innerHTML = d.message;
            rowDiv.append(dialogDiv);

            // Time
            if (prevTimeDiv?.innerHTML == currentTime) {
                prevTimeDiv.innerHTML = "";
                dialogDiv.style.marginTop = "-12px";
            }
            const timeDiv = document.createElement("div");
            timeDiv.className = d.user == userId ? "time right" : "time left";
            timeDiv.innerHTML = currentTime;
            rowDiv.append(timeDiv);
            prevTimeDiv = timeDiv;

            // Show result
            msgRef.current.append(rowDiv);
        });
        datetimeRef.current.date = prevDateDiv;
        datetimeRef.current.time = prevTimeDiv;
        msgRef.current.lastChild?.scrollIntoView();
    };

    const getContact = async () => {
        const res = await supabase.getClient().rpc("get_users");
        if (res.status == 200) setUsers(res.data);
        else console.log("Get user failed:", res);
    };

    const renderContact = (data) => {
        const rowRapper = document.querySelector(`div[channel="${data.channel}"]`);
        rowRapper.parentNode.prepend(rowRapper);
        rowRapper.querySelector(".descr").innerHTML = data.message;
        rowRapper.querySelector(".time").innerHTML = DateTime.fromISO(data.created_at).toFormat("H:m");
        if (data.channel != channel) {
            const statusIcon = document.createElement("div");
            statusIcon.className = "status-icon";
            rowRapper.querySelector(".status").innerHTML = statusIcon;
        }
    };

    const getUserByChannel = async (channelId) => {
        const res = await supabase.getClient()
            .from("users")
            .select("id")
            .eq("channel", channelId);
        if (res.status == 200) setUserId(res.data[0].id);
        else console.log("Get user failed:", res);
    };

    const createUser = async (user) => {
        const res = await supabase.getClient()
            .from("users")
            .insert(user)
            .select();
        if (res.status == 201) setUserId(res.data[0].id);
        else console.log("Create user failed:", res);
    };

    const subscribeChannel = async () => {
        await supabase.removeAllChannels();
        datetimeRef.current = { date: undefined, time: undefined };

        supabase.createChannel(userId ? channel : "subscribe-all-channels").on(
            "postgres_changes",
            {
                event: "INSERT",
                schema: "public",
                table: "messages",
                filter: userId ? `channel=eq.${channel}` : undefined
            },
            (payload) => {
                console.log("Change:", payload);
                if (channel == payload.new.channel) renderMessages([payload.new]);
                if (!userId) renderContact(payload.new);
            }
        ).subscribe(async (status) => {
            console.log("Channel:", channel, status);
        });

        // Init messages
        getMessages();
    };

    // Init supabase
    useEffect(() => {
        setSupabase(new Supabase());

        return () => {
            supabase?.removeAllChannels();
        };
    }, []);

    useEffect(() => {
        if (!supabase) return;
        initChatPanel();
    }, [supabase]);

    useEffect(() => {
        if (!supabase) return;
        if (userId) subscribeChannel();
        else {
            getMessages();
            readMessages();
        }
    }, [channel]);

    useEffect(() => {
        if (open) msgRef.current.lastChild?.scrollIntoView();
    }, [open]);

    return <>
        <FloatButton
            type="primary"
            style={{ right: 16, bottom: 16, width: "60px", height: "60px" }}
            icon={<MessageOutlined style={{ fontSize: "30px" }} />}
            tooltip={formatMessage({ id: "chat_tooltip" })}
            onClick={() => setOpen(!open)}
        />
        {!userId && <ContactWindow style={{ display: open ? "flex" : "none" }}>
            <ContactHeader />
            <ContactContent>
                {users.map((user) => <div className="row-wrapper" key={user.id} channel={user.channel} onClick={(e) => {
                    const rowRapper = e.target.closest(".row-wrapper");
                    if (e.target != rowRapper) {
                        rowRapper.parentNode.querySelectorAll(".selected").forEach((e) => e.classList.remove("selected"));
                        rowRapper.classList.add("selected");
                        setChannel(user.channel);
                    }
                }}>
                    <div className="row">
                        <div className="left-cell">
                            <div className="title">{user.name || `${formatMessage({ id: "user" })} ${user.id}`}</div>
                            <div className="descr">{user.message}</div>
                        </div>
                        <div className="right-cell">
                            <div className="time">
                                {DateTime.fromISO(user.message_time).hasSame(DateTime.now(), "day")
                                    ? DateTime.fromISO(user.message_time).toFormat("H:m")
                                    : DateTime.fromISO(user.message_time).toFormat("M/d")}
                            </div>
                            <div className="status">{!user.message_read && <div className="status-icon"/>}</div>
                        </div>
                    </div>
                </div>)}
            </ContactContent>
        </ContactWindow>}
        <MessageWindow style={{ display: open ? "flex" : "none" }}>
            <MessageHeader>
                <CloseOutlined style={{ cursor: "pointer" }} onClick={() => setOpen(!open)}/>
            </MessageHeader>
            <MessageContent>
                <div className="row-wrapper" ref={msgRef} />
            </MessageContent>
            <MessageFooter>
                <Input
                    placeholder={formatMessage({ id: "chat_placeholder" })}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    onPressEnter={() => sendMessage()}
                />
                <SendOutlined className="svg-button" onClick={() => sendMessage()}/>
            </MessageFooter>
        </MessageWindow>
    </>;
};